import React, { useEffect, useState, useCallback } from 'react';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { PasswordInput } from '../../components/ui/password-input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  KeyRound, RefreshCw, Clock, CheckCircle2, Trash2, Copy, Mail, MessageCircle, Info
} from 'lucide-react';
import { cn } from '../../lib/utils';

function generateTempPassword() {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
  let pwd = '';
  for (let i = 0; i < 8; i++) pwd += chars.charAt(Math.floor(Math.random() * chars.length));
  return pwd + '!';
}

function roleBadge(role) {
  if (role === 'admin') return 'mc-tag mc-tag-red';
  if (role === 'enseignant') return 'mc-tag mc-tag-amber';
  return 'mc-tag mc-tag-blue';
}

function ResetAndResolveDialog({ req, onDone }) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const handleGenerate = () => setPassword(generateTempPassword());

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      toast.success('Mot de passe copié');
    } catch (e) { console.warn('[PasswordResets] clipboard write failed:', e); }
  };

  const handleReset = async () => {
    if (password.length < 6) {
      toast.error('Mot de passe trop court');
      return;
    }
    setBusy(true);
    try {
      await api.resetUserPassword(req.email, password);
      await api.resolvePasswordResetRequest(req.id);
      toast.success('Mot de passe réinitialisé et demande marquée résolue');
      setDone(true);
      onDone?.();
    } catch (e) {
      toast.error(e.message || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const handleClose = (v) => {
    setOpen(v);
    if (!v) {
      setPassword('');
      setDone(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          className="rounded-full gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
          data-testid={`reset-resolve-${req.id}`}
        >
          <KeyRound className="w-4 h-4" /> Réinitialiser & résoudre
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Traiter la demande</DialogTitle>
          <DialogDescription>
            Définissez un nouveau mot de passe pour <strong>{req.user_name || req.email}</strong>.
            La demande sera automatiquement marquée comme résolue.
          </DialogDescription>
        </DialogHeader>

        {!done ? (
          <div className="space-y-3">
            <div>
              <Label htmlFor="new-pwd">Nouveau mot de passe (6 car. min.)</Label>
              <PasswordInput
                id="new-pwd"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Saisir ou générer"
                minLength={6}
                data-testid="reset-new-pwd-input"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleGenerate}
              className="rounded-full gap-1.5 w-full"
              data-testid="reset-generate-pwd"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Générer un mot de passe aléatoire
            </Button>
          </div>
        ) : (
          <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900/50 p-3">
            <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700 dark:text-emerald-300 mb-1">
              Mot de passe réinitialisé
            </div>
            <p className="text-xs text-foreground/85 mb-2">
              Transmettez ce mot de passe à <strong>{req.email}</strong> :
            </p>
            <div className="flex items-center gap-2 rounded-lg bg-white dark:bg-black/30 border border-border p-2 font-mono text-sm">
              <span className="flex-1 break-all select-all">{password}</span>
              <Button size="sm" variant="ghost" className="h-7 px-2 flex-shrink-0" onClick={handleCopy} data-testid="reset-copy-pwd">
                <Copy className="w-3.5 h-3.5" />
              </Button>
            </div>
            <a
              href={`mailto:${req.email}?subject=Nouveau%20mot%20de%20passe%20Code2Roues&body=Bonjour%2C%0A%0AVoici%20votre%20nouveau%20mot%20de%20passe%20temporaire%20%3A%20${encodeURIComponent(password)}%0A%0AMerci%20de%20le%20changer%20apr%C3%A8s%20connexion.%0A%0A%E2%80%94%20L%27%C3%A9quipe%20Code%202%20Roues`}
              className="mt-2 inline-flex items-center gap-1.5 text-xs text-[hsl(var(--primary))] hover:underline"
              data-testid="reset-mailto-link"
            >
              <Mail className="w-3.5 h-3.5" /> Envoyer par email
            </a>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => handleClose(false)} disabled={busy}>
            {done ? 'Fermer' : 'Annuler'}
          </Button>
          {!done && (
            <Button
              onClick={handleReset}
              disabled={busy || password.length < 6}
              className="mc-btn-primary"
              data-testid="reset-confirm"
            >
              Confirmer
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminPasswordResets() {
  const [data, setData] = useState({ requests: [], counts: { total: 0, pending: 0, resolved: 0 } });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getPasswordResetRequests(filter === 'all' ? null : filter);
      setData(res);
    } catch (e) {
      toast.error(e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const onDelete = async (id) => {
    if (!window.confirm('Supprimer cette demande ?')) return;
    try {
      await api.deletePasswordResetRequest(id);
      toast.success('Demande supprimée');
      load();
    } catch (e) { toast.error(e.message); }
  };

  const onResolveOnly = async (id) => {
    if (!window.confirm('Marquer cette demande comme résolue (sans réinitialiser le mot de passe) ?')) return;
    try {
      await api.resolvePasswordResetRequest(id);
      toast.success('Demande marquée résolue');
      load();
    } catch (e) { toast.error(e.message); }
  };

  const filters = [
    { k: 'pending', label: 'En attente', count: data.counts.pending },
    { k: 'resolved', label: 'Résolues', count: data.counts.resolved },
    { k: 'all', label: 'Toutes', count: data.counts.total },
  ];

  return (
    <div className="space-y-5 max-w-5xl" data-testid="admin-password-resets-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))] mb-1">
            <KeyRound className="w-3.5 h-3.5" /> Back-office admin
          </div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight">Demandes de réinitialisation</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Les utilisateurs qui cliquent sur "Mot de passe oublié" apparaissent ici. Réinitialisez
            leur mot de passe et communiquez-le-leur (email, SMS, téléphone).
          </p>
        </div>
        <Button variant="outline" size="sm" className="rounded-full gap-1.5" onClick={load} data-testid="pr-refresh-btn">
          <RefreshCw className="w-4 h-4" /> Rafraîchir
        </Button>
      </div>

      <div className="rounded-2xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-900/50 p-3">
        <div className="flex items-start gap-2.5">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-700 dark:text-amber-300" />
          <div className="text-xs text-foreground/85">
            <strong>Rappel RGPD :</strong> les mots de passe sont stockés de façon <strong>irréversible</strong> (hash bcrypt).
            Il est impossible de les afficher. La seule solution est de définir un nouveau mot de
            passe temporaire à communiquer à l'utilisateur.
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {filters.map(f => (
          <button
            key={f.k}
            onClick={() => setFilter(f.k)}
            data-testid={`pr-filter-${f.k}`}
            className={cn(
              'mc-card p-3 sm:p-4 text-left transition-all hover:-translate-y-0.5',
              filter === f.k ? 'ring-2 ring-[hsl(var(--primary))] ring-offset-2 ring-offset-background' : ''
            )}
          >
            <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">{f.label}</div>
            <div className="text-2xl sm:text-3xl font-black mt-0.5 tabular-nums">{f.count}</div>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mc-card p-8 text-center text-muted-foreground">Chargement…</div>
      ) : data.requests.length === 0 ? (
        <div className="mc-card p-8 text-center" data-testid="pr-empty">
          <KeyRound className="w-10 h-10 mx-auto text-muted-foreground mb-3 opacity-40" />
          <div className="font-bold text-lg">Aucune demande {filter === 'pending' ? 'en attente' : filter === 'resolved' ? 'résolue' : ''}</div>
          <p className="text-sm text-muted-foreground mt-1">Les demandes "mot de passe oublié" apparaîtront ici.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.requests.map(req => (
            <div key={req.id} className="mc-card p-4 space-y-3" data-testid={`pr-card-${req.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h3 className="text-base font-black">{req.user_name || '(sans nom)'}</h3>
                    <span className={roleBadge(req.user_role)}>{req.user_role}</span>
                    {req.status === 'pending' ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest border bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 border-amber-200 dark:border-amber-900/50">
                        <Clock className="w-3 h-3" /> En attente
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest border bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200 border-emerald-200 dark:border-emerald-900/50">
                        <CheckCircle2 className="w-3 h-3" /> Résolue
                      </span>
                    )}
                  </div>
                  <a href={`mailto:${req.email}`} className="text-sm font-semibold hover:underline break-all block">{req.email}</a>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Demande du {new Date(req.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </div>
                  {req.resolved_at && (
                    <div className="text-xs text-muted-foreground">
                      Résolue le {new Date(req.resolved_at).toLocaleDateString('fr-FR')} par {req.resolved_by}
                    </div>
                  )}
                </div>

                <div className="flex gap-2 flex-wrap flex-shrink-0">
                  {req.status === 'pending' ? (
                    <>
                      <ResetAndResolveDialog req={req} onDone={load} />
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-full gap-1.5"
                        onClick={() => onResolveOnly(req.id)}
                        data-testid={`pr-resolve-only-${req.id}`}
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" /> Marquer résolue
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="rounded-full gap-1.5 text-muted-foreground hover:text-rose-600"
                      onClick={() => onDelete(req.id)}
                      data-testid={`pr-delete-${req.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Supprimer
                    </Button>
                  )}
                </div>
              </div>

              {req.message && (
                <div className="rounded-xl bg-[hsl(var(--panel))] border border-border p-3">
                  <div className="flex items-start gap-2">
                    <MessageCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-muted-foreground" />
                    <div>
                      <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-1">Message</div>
                      <p className="text-sm text-foreground/90 whitespace-pre-wrap">{req.message}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
